import {Skeleton} from '@mantine/core';
import {forwardRef} from 'react';
import {MdArrowForward, MdArrowForwardIos} from 'react-icons/md';
import type {z} from 'zod';
import type {
  CompanySchema,
  CustomTagSchema,
} from '../../../../core/entities/company/schema';
import {cn} from '../../../../lib/classname';
import {CommonLink} from '../../../../shared/components/CommonLink';
import {CompanyCard} from '../Container/Presenter/CompanyCard';
import {COMPANY_LIST_LIMIT} from './constants';

type Props = {
  componentID: string;
  isLoading: boolean;
  customTag: z.infer<typeof CustomTagSchema>;
  companies: z.infer<typeof CompanySchema>[];
  withOdd: boolean;
};

type SkeletonProps = {
  withOdd: boolean;
};

const CustomTagCompaniesFrameSkeleton: React.FC<SkeletonProps> = ({
  withOdd,
}) => {
  return (
    <div
      className={cn('w-full pt-16 pl-16 pc:pl-0', {
        'odd:bg-white': withOdd,
      })}
    >
      <div className="mx-auto pc:w-[1200px]">
        <div
          className={cn({
            'pc:p-16 pc:bg-white pc:rounded-16 pc:shadow-card': !withOdd,
          })}
        >
          <div className="flex items-center">
            <Skeleton height={24} width={200} />
            <span className="p-4">
              <Skeleton height={20} width={20} />
            </span>
          </div>
          <div className="mt-8">
            <Skeleton
              height={16}
              width={300}
              className={cn({
                'hidden pc:block': !withOdd,
              })}
            />
          </div>
        </div>
        <div className="flex mt-8 py-8 mb-8 gap-16 pc:gap-24 overflow-x-scroll">
          {Array.from({length: 5}).map((_, index) => (
            <div key={`skeleton-${index}`}>
              <Skeleton height={150} width={300} />
              <Skeleton height={30} width={300} />
              <Skeleton height={30} width={300} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const CustomTagCompaniesFramePresentation = forwardRef<
  HTMLDivElement,
  Props
>(({componentID, isLoading, customTag, companies, withOdd}, ref) => {
  return (
    <div
      id={componentID}
      ref={ref}
      className={cn('w-full pt-16 pl-16 pc:pl-0', {
        'odd:bg-white': withOdd,
      })}
    >
      <div className="mx-auto pc:w-[1200px]">
        {companies.length > 0 && (
          <>
            <div
              className={cn({
                'pc:p-16 pc:bg-white pc:rounded-16 pc:shadow-card': !withOdd,
              })}
            >
              <CommonLink
                href={`/company/feature/${customTag.id}`}
                className="flex hover:opacity-70"
              >
                <h2 className="font-bold text-black text-xl">
                  {customTag.name}
                </h2>
                <span className="p-4">
                  <MdArrowForwardIos size={20} />
                </span>
              </CommonLink>
              <div>
                <p
                  className={cn('text-black-3', {
                    'hidden pc:block': !withOdd,
                  })}
                >
                  {customTag.description}
                </p>
              </div>
            </div>
            <div className="flex mt-8 py-8 mb-8 gap-16 pc:gap-24 overflow-x-scroll">
              {companies.map((company) => (
                <CompanyCard
                  key={`custom-card-${company.id}`}
                  company={company}
                  width={300}
                  bgColorStyle={'pc:bg-transparent'}
                />
              ))}
              {companies.length >= COMPANY_LIST_LIMIT && (
                <CommonLink
                  href={`/company/feature/${customTag.id}`}
                  className="shadow-card bg-white p-16 rounded-16 hover:opacity-70 m-4 flex items-center"
                >
                  <div className="flex justify-center py-16">
                    <MdArrowForward size={40} />
                  </div>
                  <div>
                    <div className="text-md text-black text-center w-[200px]">
                      もっと見る
                    </div>
                  </div>
                </CommonLink>
              )}
              {isLoading &&
                Array.from({length: 5}).map((_, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: order won't change
                  <div key={index}>
                    <Skeleton height={150} width={300} />
                    <Skeleton height={30} width={300} />
                    <Skeleton height={30} width={300} />
                  </div>
                ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
});

CustomTagCompaniesFramePresentation.displayName =
  'CustomTagCompaniesFramePresentation';

export {CustomTagCompaniesFrameSkeleton};
